// Super_admin-only top-level page. Lists Publica push jobs across all
// stations so staff can audit failed/partial pushes that the consumer UI
// intentionally hides.

import React from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';
import { AlertTriangle, ArrowLeft, ChevronRight, Inbox, Loader2 } from 'lucide-react';
import { useAuth } from '../../components/self-service/auth/useAuth';
import { apiClient, ApiError } from '../../lib/apiClient';
import { formatDateTime } from '../../components/self-service/shared/dateFormat';
import { statusPillClass, type PushJobStatus } from './publicaPushJobHelpers';

type JobStatus = PushJobStatus;

interface PushJobListRow {
  id: string;
  selfservice_submission_id: string;
  organization_id: string | null;
  station_id: string | null;
  station_slug: string | null;
  station_name: string | null;
  advertiser_name: string | null;
  campaign_name: string | null;
  job_kind: 'create' | 'edit' | 'creative_swap' | 'refund_cleanup';
  status: JobStatus;
  retry_count: number;
  max_retries: number;
  last_error: string | null;
  failed_step: string | null;
  line_items_completed: number | null;
  line_items_total: number | null;
  created_at: string;
  started_at: string | null;
  completed_at: string | null;
  updated_at: string;
}

type TabKey = 'failed' | 'partial' | 'processing' | 'completed' | 'all';

const TABS: { key: TabKey; label: string; status?: JobStatus[] }[] = [
  { key: 'failed', label: 'Failed', status: ['failed'] },
  { key: 'partial', label: 'Partial', status: ['partial'] },
  { key: 'processing', label: 'Processing', status: ['pending', 'processing'] },
  { key: 'completed', label: 'Completed', status: ['completed'] },
  { key: 'all', label: 'All', status: undefined },
];

const SINCE_OPTIONS = [7, 14, 30, 90];

const PublisherPublicaPushJobsPage: React.FC = () => {
  const location = useLocation();
  const { user, isAuthenticated } = useAuth();

  const [rows, setRows] = React.useState<PushJobListRow[]>([]);
  const [total, setTotal] = React.useState(0);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [tab, setTab] = React.useState<TabKey>('failed');
  const [stationSlug, setStationSlug] = React.useState('');
  const [submissionId, setSubmissionId] = React.useState('');
  const [sinceDays, setSinceDays] = React.useState(14);

  const refresh = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const body: Record<string, unknown> = { sinceDays, limit: 100 };
      const matched = TABS.find((t) => t.key === tab);
      if (matched?.status) body.status = matched.status;
      if (stationSlug.trim()) body.stationSlug = stationSlug.trim();
      if (submissionId.trim()) body.submissionId = submissionId.trim();
      const data = await apiClient.post<{ rows: PushJobListRow[]; total: number }>(
        '/publica-push-jobs/list',
        body,
      );
      setRows(data.rows ?? []);
      setTotal(data.total ?? 0);
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : e instanceof Error ? e.message : 'Failed to load push jobs';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [tab, stationSlug, submissionId, sinceDays]);

  React.useEffect(() => {
    refresh();
    const id = setInterval(refresh, 30_000);
    return () => clearInterval(id);
  }, [refresh]);

  if (!isAuthenticated || !user) {
    return (
      <Navigate
        to="/admin"
        state={{ from: location.pathname + location.search }}
        replace
      />
    );
  }
  if (user.rawRole !== 'super_admin') {
    return <Navigate to="/admin" replace />;
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 md:px-8 py-8 md:py-12">
        <Link
          to="/admin"
          className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900 mb-4"
        >
          <ArrowLeft className="w-4 h-4" /> Back to admin
        </Link>

        <div className="flex items-center justify-between mb-5">
          <div>
            <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Publica push jobs
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Super-admin audit of Publica push jobs across every channel.
            </p>
          </div>
          <button onClick={refresh} className="text-sm text-slate-600 hover:text-slate-900">
            Refresh
          </button>
        </div>

        <div className="border-b border-slate-200 mb-5">
          <nav className="flex gap-1 flex-wrap">
            {TABS.map((t) => (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                  tab === t.key
                    ? 'border-slate-900 text-slate-900'
                    : 'border-transparent text-slate-600 hover:text-slate-900'
                }`}
              >
                {t.label}
              </button>
            ))}
          </nav>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-5">
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Channel slug</label>
            <input
              type="text"
              value={stationSlug}
              onChange={(e) => setStationSlug(e.target.value)}
              placeholder="e.g. wkbn"
              className="w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-900"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Submission id</label>
            <input
              type="text"
              value={submissionId}
              onChange={(e) => setSubmissionId(e.target.value)}
              placeholder="uuid"
              className="w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-900 font-mono"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Window</label>
            <select
              value={sinceDays}
              onChange={(e) => setSinceDays(Number(e.target.value))}
              className="w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-900"
            >
              {SINCE_OPTIONS.map((d) => (
                <option key={d} value={d}>Last {d} days</option>
              ))}
            </select>
          </div>
        </div>

        {error && <p className="mb-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded p-3">{error}</p>}

        {loading ? (
          <div className="text-sm text-slate-500">
            <Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…
          </div>
        ) : rows.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-xl p-10 text-center">
            <Inbox className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-700 font-medium">No push jobs match this filter</p>
            <p className="text-sm text-slate-500 mt-1">Try a different status tab or widen the window.</p>
          </div>
        ) : (
          <>
            <p className="text-xs text-slate-500 mb-2">{rows.length} of {total} shown</p>
            <ul className="space-y-2">
              {rows.map((r) => (
                <li key={r.id}>
                  <Link
                    to={`/admin/publica-push-jobs/${r.id}`}
                    className="flex items-center justify-between gap-3 bg-white border border-slate-200 rounded-xl p-4 hover:border-slate-400 hover:shadow-sm transition-all"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="text-base font-semibold text-slate-900 truncate">
                          {r.campaign_name || r.advertiser_name || r.selfservice_submission_id}
                        </span>
                        <span className={`text-xs font-medium px-2 py-0.5 rounded ${statusPillClass(r.status)}`}>
                          {r.status}
                        </span>
                        <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 border border-slate-200">
                          {r.job_kind}
                        </span>
                        {r.failed_step && (
                          <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-red-50 text-red-700 border border-red-200">
                            failed at: {r.failed_step}
                          </span>
                        )}
                        {r.line_items_total != null && (
                          <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-slate-50 text-slate-700 border border-slate-200">
                            line items: {r.line_items_completed ?? 0}/{r.line_items_total}
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-slate-500 flex gap-3 flex-wrap">
                        <span>{r.station_slug ?? '—'}</span>
                        <span>{r.advertiser_name ?? '—'}</span>
                        <span>retries {r.retry_count}/{r.max_retries}</span>
                        <span>{formatDateTime(r.created_at)}</span>
                      </div>
                      {r.last_error && (
                        <div className="text-xs text-red-700 mt-1 truncate">{r.last_error}</div>
                      )}
                    </div>
                    <ChevronRight className="w-5 h-5 text-slate-400 flex-shrink-0" />
                  </Link>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    </div>
  );
};

export default PublisherPublicaPushJobsPage;
